import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const AdminReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchReport();
  }, [user, navigate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/reports');
      setReport(response.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.error || 'Erro ao carregar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .ar-loading { min-height: 100vh; background: #f5f5f5; display: flex; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; }
          .ar-loading-spinner { width: 50px; height: 50px; border: 4px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
          @keyframes spin { to { transform: rotate(360deg); } }
          .ar-loading p { color: #666; font-weight: 500; }
        `}</style>
        <div className="ar-loading">
          <div style={{ textAlign: 'center' }}>
            <div className="ar-loading-spinner"></div>
            <p>Carregando relatório...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .ar *, .ar *::before, .ar *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .ar { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }

        /* ===== HEADER ===== */
        .ar-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .ar-header-logo { width: 40px; height: 40px; flex-shrink: 0; }
        .ar-header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .ar-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; }
        .ar-header-left { display: flex; align-items: center; gap: 20px; }
        .ar-header-back, .ar-header-refresh {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500; background: none; border: none; font-family: inherit; font-size: 14px;
        }
        .ar-header-back:hover { color: #fff; transform: translateX(-3px); }
        .ar-header-refresh:hover { color: #fff; }

        /* ===== MAIN ===== */
        .ar-main { max-width: 1100px; margin: 0 auto; padding: 35px 20px; }

        /* ===== STATUS BANNER ===== */
        .ar-banner {
          padding: 28px 30px; border-radius: 16px; margin-bottom: 28px;
          display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
        }
        .ar-banner.ok {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1px solid rgba(34,197,94,0.2);
        }
        .ar-banner.error {
          background: linear-gradient(135deg, #fef2f2, #fee2e2);
          border: 1px solid rgba(239,68,68,0.2);
        }
        .ar-banner-icon {
          width: 60px; height: 60px; border-radius: 16px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ar-banner.ok .ar-banner-icon { background: rgba(34,197,94,0.15); }
        .ar-banner.ok .ar-banner-icon i { font-size: 28px; color: #22c55e; }
        .ar-banner.error .ar-banner-icon { background: rgba(239,68,68,0.15); }
        .ar-banner.error .ar-banner-icon i { font-size: 28px; color: #ef4444; }
        .ar-banner-text h3 { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
        .ar-banner.ok .ar-banner-text h3 { color: #16a34a; }
        .ar-banner.error .ar-banner-text h3 { color: #dc2626; }
        .ar-banner-text p { font-size: 14px; color: #666; }

        /* ===== TOTALS GRID ===== */
        .ar-totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 28px; }
        .ar-total-icon.manual { background: linear-gradient(135deg, #faf5ff, #f3e8ff); }
        .ar-total-icon.manual i { color: #a855f7; font-size: 22px; }
        .ar-total-card {
          background: #fff; padding: 24px; border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
          text-align: center; transition: all 0.3s;
        }
        .ar-total-card:hover { transform: translateY(-3px); box-shadow: 0 8px 30px rgba(0,0,0,0.1); }
        .ar-total-icon {
          width: 56px; height: 56px; border-radius: 14px; margin: 0 auto 14px;
          display: flex; align-items: center; justify-content: center;
        }
        .ar-total-icon.sold { background: linear-gradient(135deg, #eff6ff, #dbeafe); }
        .ar-total-icon.sold i { color: #3b82f6; font-size: 22px; }
        .ar-total-icon.codes { background: linear-gradient(135deg, #fff7ed, #ffedd5); }
        .ar-total-icon.codes i { color: #f97316; font-size: 22px; }
        .ar-total-icon.balance { background: linear-gradient(135deg, #f0fdf4, #dcfce7); }
        .ar-total-icon.balance i { color: #22c55e; font-size: 22px; }
        .ar-total-icon.balance.negative { background: linear-gradient(135deg, #fef2f2, #fee2e2); }
        .ar-total-icon.balance.negative i { color: #ef4444; }
        .ar-total-label { font-size: 12px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .ar-total-value { font-size: 32px; font-weight: 700; color: #1e293b; }
        .ar-total-value.negative { color: #ef4444; }
        .ar-total-desc { font-size: 12px; color: #94a3b8; margin-top: 4px; }

        /* ===== TABLE ===== */
        .ar-section { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04); overflow: hidden; margin-bottom: 28px; }
        .ar-section-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
        .ar-section-title { font-size: 16px; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 10px; }
        .ar-section-title i { color: #f97316; }
        .ar-section-badge { font-size: 12px; padding: 4px 12px; border-radius: 20px; font-weight: 600; }
        .ar-section-badge.ok { background: #dcfce7; color: #16a34a; }
        .ar-section-badge.error { background: #fee2e2; color: #dc2626; }

        .ar-table-wrap { overflow-x: auto; }
        .ar-table { width: 100%; border-collapse: collapse; }
        .ar-table th {
          text-align: left; padding: 14px 20px; font-size: 11px; font-weight: 700;
          color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;
          background: #f8fafc; border-bottom: 2px solid #f1f5f9;
        }
        .ar-table td {
          padding: 14px 20px; font-size: 14px; color: #475569;
          border-bottom: 1px solid #f1f5f9; vertical-align: middle;
        }
        .ar-table tbody tr:hover { background: #fafbfc; }
        .ar-table tbody tr.error-row { background: #fef2f2; }
        .ar-table tbody tr.error-row:hover { background: #fee2e2; }

        .ar-user-info { display: flex; align-items: center; gap: 12px; }
        .ar-user-avatar {
          width: 36px; height: 36px; border-radius: 10px;
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; color: #f97316; flex-shrink: 0;
        }
        .ar-user-name { font-weight: 600; color: #1e293b; }
        .ar-user-email { font-size: 12px; color: #94a3b8; }

        .ar-badge-ok { background: #dcfce7; color: #16a34a; font-size: 12px; padding: 4px 10px; border-radius: 6px; font-weight: 600; display: inline-block; }
        .ar-badge-error { background: #fee2e2; color: #dc2626; font-size: 12px; padding: 4px 10px; border-radius: 6px; font-weight: 600; display: inline-block; }
        .ar-badge-warn { background: #fef3c7; color: #d97706; font-size: 12px; padding: 4px 10px; border-radius: 6px; font-weight: 600; display: inline-block; }

        /* ===== ERRORS SECTION ===== */
        .ar-errors-section { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(239,68,68,0.15); overflow: hidden; }
        .ar-errors-header { padding: 20px 24px; border-bottom: 1px solid #fee2e2; background: linear-gradient(135deg, #fef2f2, #fff); display: flex; align-items: center; gap: 12px; }
        .ar-errors-header i { color: #ef4444; font-size: 18px; }
        .ar-errors-header h3 { font-size: 16px; font-weight: 700; color: #dc2626; }

        /* ===== EMPTY ===== */
        .ar-empty { padding: 50px; text-align: center; }
        .ar-empty-icon { width: 70px; height: 70px; border-radius: 50%; margin: 0 auto 15px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; }
        .ar-empty-icon i { font-size: 28px; color: #ccc; }
        .ar-empty p { color: #999; font-size: 15px; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 768px) {
          .ar-header { padding: 15px 20px; }
          .ar-header-title { font-size: 18px; }
          .ar-main { padding: 20px 15px; }
          .ar-totals { grid-template-columns: repeat(2, 1fr); }
          .ar-banner { flex-direction: column; text-align: center; padding: 20px; }
          .ar-table th, .ar-table td { padding: 12px 14px; font-size: 12px; }
        }
        @media (max-width: 480px) {
          .ar-header { padding: 12px 15px; }
          .ar-header-title { font-size: 16px; }
          .ar-main { padding: 15px 10px; }
          .ar-total-value { font-size: 26px; }
          .ar-table th, .ar-table td { padding: 10px 10px; font-size: 11px; }
        }
      `}</style>

      <div className="ar">
        {/* Header */}
        <header className="ar-header">
          <div className="ar-header-logo">
            <img src="/images/comex.png" alt="COMEX" />
          </div>
          <h1 className="ar-header-title">Relatórios</h1>
          <div className="ar-header-left">
            <button className="ar-header-refresh" onClick={fetchReport}>
              <i className="fas fa-sync-alt"></i> Atualizar
            </button>
            {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
          </div>
        </header>

        <main className="ar-main">
          {error && (
            <div className="ar-banner error">
              <div className="ar-banner-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="ar-banner-text">
                <h3>Erro ao carregar relatório</h3>
                <p>{error}</p>
              </div>
              <button onClick={fetchReport} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, marginLeft: 'auto' }}>Tentar Novamente</button>
            </div>
          )}
          {report && (
            <>
              {/* Status Banner */}
              <div className={`ar-banner ${report.hasError ? 'error' : 'ok'}`}>
                <div className="ar-banner-icon">
                  <i className={`fas ${report.hasError ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
                </div>
                <div className="ar-banner-text">
                  <h3>{report.hasError ? 'Inconsistência Detectada!' : 'Sistema OK - Sem Erros'}</h3>
                  <p>{report.hasError
                    ? `Foram gerados ${Math.abs(report.difference)} códigos a mais do que créditos recebidos por usuários.`
                    : 'O total de códigos gerados pelos usuários é menor ou igual ao total de créditos recebidos (comprados + manuais).'
                  }</p>
                </div>
              </div>

              {/* Totals */}
              <div className="ar-totals">
                <div className="ar-total-card">
                  <div className="ar-total-icon sold"><i className="fas fa-shopping-cart"></i></div>
                  <div className="ar-total-label">Créditos Comprados</div>
                  <div className="ar-total-value">{report.totalCreditsPurchased}</div>
                  <div className="ar-total-desc">Compras aprovadas</div>
                </div>
                <div className="ar-total-card">
                  <div className="ar-total-icon manual"><i className="fas fa-user-shield"></i></div>
                  <div className="ar-total-label">Créditos pelo Admin</div>
                  <div className="ar-total-value">{report.totalCreditsManual}</div>
                  <div className="ar-total-desc">Adicionados manualmente</div>
                </div>
                <div className="ar-total-card">
                  <div className="ar-total-icon codes"><i className="fas fa-qrcode"></i></div>
                  <div className="ar-total-label">Códigos Gerados</div>
                  <div className="ar-total-value">{report.totalCodesGenerated}</div>
                  <div className="ar-total-desc">Total por usuários</div>
                </div>
                <div className="ar-total-card">
                  <div className={`ar-total-icon balance ${report.hasError ? 'negative' : ''}`}>
                    <i className={`fas ${report.hasError ? 'fa-times-circle' : 'fa-check-circle'}`}></i>
                  </div>
                  <div className="ar-total-label">Saldo</div>
                  <div className={`ar-total-value ${report.hasError ? 'negative' : ''}`}>
                    {report.hasError ? `-${Math.abs(report.difference)}` : `+${report.totalCredits - report.totalCodesGenerated}`}
                  </div>
                  <div className="ar-total-desc">{report.hasError ? 'Códigos em excesso' : 'Créditos restantes'}</div>
                </div>
              </div>

              {/* All Users Table */}
              <div className="ar-section">
                <div className="ar-section-header">
                  <div className="ar-section-title">
                    <i className="fas fa-users"></i> Relatório por Usuário
                  </div>
                  <span className={`ar-section-badge ${report.hasError ? 'error' : 'ok'}`}>
                    {report.users.length} usuários
                  </span>
                </div>
                {report.users.length > 0 ? (
                  <div className="ar-table-wrap">
                    <table className="ar-table">
                      <thead>
                        <tr>
                          <th>Usuário</th>
                          <th>Comprados</th>
                          <th>Pelo Admin</th>
                          <th>Total Créditos</th>
                          <th>Códigos Gerados</th>
                          <th>Saldo Atual</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.users.map(u => {
                          const totalCred = u.credits_purchased + u.credits_manual;
                          const hasErr = u.total_codes_generated > totalCred;
                          return (
                            <tr key={u.id} className={hasErr ? 'error-row' : ''}>
                              <td>
                                <div className="ar-user-info">
                                  <div className="ar-user-avatar">{u.username.charAt(0).toUpperCase()}</div>
                                  <div>
                                    <div className="ar-user-name">{u.username}</div>
                                    <div className="ar-user-email">{u.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td><strong>{u.credits_purchased}</strong></td>
                              <td><strong style={{ color: '#a855f7' }}>{u.credits_manual}</strong></td>
                              <td><strong>{totalCred}</strong></td>
                              <td><strong>{u.total_codes_generated}</strong></td>
                              <td><strong>{u.credits}</strong></td>
                              <td>
                                {hasErr
                                  ? <span className="ar-badge-error"><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i> Excedeu em {u.total_codes_generated - totalCred}</span>
                                  : u.total_codes_generated === totalCred
                                    ? <span className="ar-badge-warn"><i className="fas fa-equals" style={{ marginRight: 4 }}></i> Zerado</span>
                                    : <span className="ar-badge-ok"><i className="fas fa-check" style={{ marginRight: 4 }}></i> OK</span>
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ar-empty">
                    <div className="ar-empty-icon"><i className="fas fa-user-slash"></i></div>
                    <p>Nenhum usuário encontrado</p>
                  </div>
                )}
              </div>

              {/* Error Users */}
              {report.usersWithErrors.length > 0 && (
                <div className="ar-errors-section">
                  <div className="ar-errors-header">
                    <i className="fas fa-exclamation-circle"></i>
                    <h3>Usuários com Inconsistências ({report.usersWithErrors.length})</h3>
                  </div>
                  <div className="ar-table-wrap">
                    <table className="ar-table">
                      <thead>
                        <tr>
                          <th>Usuário</th>
                          <th>Comprados</th>
                          <th>Pelo Admin</th>
                          <th>Total Créditos</th>
                          <th>Códigos Gerados</th>
                          <th>Excesso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.usersWithErrors.map(u => (
                          <tr key={u.id} className="error-row">
                            <td>
                              <div className="ar-user-info">
                                <div className="ar-user-avatar" style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', color: '#ef4444' }}>
                                  {u.username.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="ar-user-name">{u.username}</div>
                                  <div className="ar-user-email">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><strong>{u.credits_purchased}</strong></td>
                            <td><strong style={{ color: '#a855f7' }}>{u.credits_manual}</strong></td>
                            <td><strong>{u.total_credits}</strong></td>
                            <td><strong style={{ color: '#ef4444' }}>{u.total_codes_generated}</strong></td>
                            <td><span className="ar-badge-error">+{u.difference} códigos</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminReports;
